/** Shared visual tokens for the sales report dashboard (brand-aligned, subtle). */

export const brand = {
  slate: '#37474F',
  slateLight: '#546E7A',
  slateMuted: '#90A4AE',
  teal: '#00897b',
  tealLight: 'rgba(0, 137, 123, 0.12)',
  purple: '#6a1b9a',
  purpleLight: 'rgba(106, 27, 154, 0.08)',
} as const;

/** Visible section borders — stronger than default MUI divider. */
export const borders = {
  strong: 'rgba(55, 71, 79, 0.32)',
  medium: 'rgba(55, 71, 79, 0.22)',
  light: 'rgba(55, 71, 79, 0.16)',
} as const;

export const dashboardSalesStyles = {
  pageBackground: {
    minHeight: '100vh',
    background: `linear-gradient(180deg, rgba(55, 71, 79, 0.06) 0%, rgba(144, 164, 174, 0.04) 48px, transparent 200px)`,
  },
  mainCard: {
    borderRadius: 3,
    overflow: 'hidden',
    border: '1px solid',
    borderColor: borders.strong,
    boxShadow: '0 4px 24px rgba(55, 71, 79, 0.1)',
    bgcolor: 'background.paper',
  },
  header: {
    px: { xs: 2, sm: 3 },
    py: 2.5,
    background: brand.slate,
    // background: `linear-gradient(135deg, ${brand.slate} 0%, ${brand.slateLight} 55%, #78909c 100%)`,
    color: 'common.white',
  },
  headerSubtitle: {
    color: 'rgba(255, 255, 255, 0.85)',
    mt: 0.5,
  },
  exportButton: {
    minWidth: { sm: 160 },
    flexShrink: 0,
    bgcolor: 'common.white',
    color: brand.slate,
    fontWeight: 600,
    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.92)' },
    '&.Mui-disabled': {
      bgcolor: 'rgba(255, 255, 255, 0.5)',
      color: 'rgba(55, 71, 79, 0.5)',
    },
  },
  filtersPanel: {
    px: { xs: 2, sm: 3 },
    py: 2.5,
    borderBottom: '2px solid',
    borderColor: borders.medium,
    background: `linear-gradient(180deg, rgba(144, 164, 174, 0.14) 0%, rgba(144, 164, 174, 0.05) 100%)`,
  },
  chartsSection: {
    px: { xs: 2, sm: 3 },
    pt: 3,
    pb: 3,
    bgcolor: 'rgba(248, 250, 252, 0.95)',
    // borderTop: '2px solid',
    borderTopColor: borders.light,
  },
  optionsSection: {
    px: { xs: 2, sm: 3 },
    py: 3,
  },
  sectionTitle: {
    fontWeight: 700,
    color: brand.slate,
    letterSpacing: '-0.01em',
  },
  presetChip: {
    cursor: 'pointer',
    fontWeight: 500,
    borderColor: 'rgba(55, 71, 79, 0.25)',
    '&:hover': {
      bgcolor: 'rgba(55, 71, 79, 0.08)',
      borderColor: brand.slate,
    },
  },
  chartCard: {
    height: '100%',
    borderRadius: 2,
    border: '1px solid',
    borderColor: borders.strong,
    boxShadow: '0 2px 8px rgba(55, 71, 79, 0.08)',
    overflow: 'hidden',
  },
  chartCardHeader: (accent: string) => ({
    px: 2,
    py: 1.25,
    borderBottom: '1px solid',
    borderBottomColor: borders.medium,
    bgcolor: 'rgba(55, 71, 79, 0.03)',
    borderLeft: `4px solid ${accent}`,
  }),
} as const;

export function panelSectionSx(accentColor: string = brand.slate) {
  return {
    p: { xs: 1.75, sm: 2 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: borders.strong,
    borderLeft: `4px solid ${accentColor}`,
    bgcolor: 'background.paper',
    boxShadow: '0 1px 4px rgba(55, 71, 79, 0.06)',
  };
}

export function scopeSectionSx() {
  return {
    p: { xs: 1.75, sm: 2 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: borders.medium,
    borderLeft: `4px solid ${brand.slateMuted}`,
    bgcolor: 'background.paper',
  };
}

export function nestedCardSx(accentColor: string = brand.slate) {
  return {
    borderRadius: 2,
    border: '1px solid',
    borderColor: borders.medium,
    borderLeft: `3px solid ${accentColor}`,
    bgcolor: 'rgba(55, 71, 79, 0.02)',
    boxShadow: 'none',
  };
}

export type KpiAccent = 'slate' | 'teal' | 'blue' | 'green' | 'purple';

const kpiAccents: Record<
  KpiAccent,
  { border: string; bg: string; value: string }
> = {
  slate: {
    border: brand.slate,
    bg: 'rgba(55, 71, 79, 0.08)',
    value: brand.slate,
  },
  teal: { border: brand.teal, bg: brand.tealLight, value: brand.teal },
  blue: { border: '#1976d2', bg: 'rgba(25, 118, 210, 0.1)', value: '#1565c0' },
  green: { border: '#2e7d32', bg: 'rgba(46, 125, 50, 0.1)', value: '#2e7d32' },
  purple: { border: brand.purple, bg: brand.purpleLight, value: brand.purple },
};

export function kpiCardSx(accent: KpiAccent) {
  const c = kpiAccents[accent];
  return {
    height: '100%',
    borderRadius: 2,
    border: '1px solid',
    borderColor: borders.strong,
    borderLeft: `4px solid ${c.border}`,
    bgcolor: c.bg,
    boxShadow: '0 2px 8px rgba(55, 71, 79, 0.08)',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(55, 71, 79, 0.1)',
    },
    '& .kpi-value': { color: c.value },
  };
}

export function sectionCardSx(accentColor: string) {
  return nestedCardSx(accentColor);
}
